/**
 * Audit: freeze today's rule evaluation into a write-once snapshot (kit route
 * /compliance/audits/:id/snapshot). Each snapshot lists the table versions it used, so it reads
 * the same next year even after the rules change.
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Collapse, Empty, Popconfirm, Row, Space, Statistic, Table, Tag, Tooltip, Typography, message } from "antd";
import { LockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { kernel, type Finding } from "../api";
import { FindingsTable } from "../findings";
import { LINKS_CHANGED, evaluation } from "./obligations";

interface Snapshot {
  id: string; audit_id: string; generated_at: string;
  tables: Array<{ table: string; sha256: string; validated_by: string }>;
  summary: { findings: number; met: number; unmet: number; blocking: number; draft: number };
  findings: Finding[];
}

export const AuditSnapshots: React.FC<{ record: Record<string, unknown> }> = ({ record }) => {
  const id = String(record.id);
  const [list, setList] = useState<Snapshot[]>();
  const [busy, setBusy] = useState(false);
  const load = () => kernel<Snapshot[]>(`/compliance/audits/${encodeURIComponent(id)}/snapshots`).then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const freeze = async () => {
    setBusy(true);
    try {
      await kernel<Snapshot>(`/compliance/audits/${encodeURIComponent(id)}/snapshot`, {});
      evaluation(true);
      window.dispatchEvent(new Event(LINKS_CHANGED));
      message.success("Verifica congelata");
      await load();
    } catch (e) { message.error((e as Error).message); } finally { setBusy(false); }
  };
  return <Card size="small" style={{ marginTop: 16 }} loading={!list}
    title={<Space><LockOutlined />Verifiche congelate</Space>}
    extra={<Popconfirm title="Congelare la verifica di oggi?" description="Lo snapshot non potrà essere modificato." onConfirm={freeze}>
      <Button size="small" type="primary" loading={busy}>Congela la verifica di oggi</Button></Popconfirm>}>
    {list && list.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessuno snapshot: congela la verifica alla data dell'audit." />
      : <Collapse accordion items={(list ?? []).map((s, k) => ({
          key: s.id,
          label: <Space>{dayjs(s.generated_at).format("DD/MM/YYYY HH:mm")}{k === 0 && <Tag color="blue">ultimo</Tag>}
            <Tag color={s.summary.blocking ? "red" : "green"}>{s.summary.blocking} bloccanti</Tag>
            <Typography.Text type="secondary">{s.summary.met}/{s.summary.findings} obblighi soddisfatti</Typography.Text></Space>,
          children: <>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              <Col span={6}><Statistic title="Copertura" value={s.summary.findings ? Math.round((s.summary.met / s.summary.findings) * 100) : 100} suffix="%" /></Col>
              <Col span={6}><Statistic title="Da sanare" value={s.summary.unmet} /></Col>
              <Col span={6}><Statistic title="Bloccanti" value={s.summary.blocking} valueStyle={{ color: s.summary.blocking ? "#cf1322" : undefined }} /></Col>
              <Col span={6}><Statistic title="Da regole in bozza" value={s.summary.draft} /></Col>
            </Row>
            {s.summary.draft > 0 && <Alert style={{ marginBottom: 12 }} type="warning" showIcon message="Alcune regole non erano ancora validate da DPO/RSPP alla data dello snapshot." />}
            <FindingsTable findings={s.findings} />
            <Table size="small" style={{ marginTop: 12 }} rowKey="table" pagination={false} dataSource={s.tables}
              columns={[
                { title: "Tabella", dataIndex: "table" },
                { title: "Versione", dataIndex: "sha256", render: (h: string) => <Tooltip title={h}><code>{h.slice(0, 12)}</code></Tooltip> },
                { title: "Validata da", dataIndex: "validated_by", render: (v: string) => v || <Tag>bozza</Tag> },
              ]} />
          </>,
        }))} />}
  </Card>;
};
